<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'InvoiceLineMeterReadingSelector',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    rental: {
      type: String,
      required: true,
    },
    multiple: {
      type: Boolean,
      required: false,
    },
    serviceId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getPayload = (...args) => {
    const [data] = args;
    const fields = this.target && this.targetConfig[this.target].fieldsToSubmit;
    return fields ? _.pick(data, fields) : data;
  };
  const isServiceStale = (...args) => {
    const [service] = args;
    return (
      service.valid_to &&
      $fn.value.parseISODate(service.valid_to) < $fn.value.today
    );
  };
  const onCheckChanged = (...args) => {
    const [service] = args;

    const selection = _.chain(service.readings)
      .filter({ selected: true })
      .reverse()
      .value();

    const consumptions = [];

    selection.forEach((item, index) => {
      const nextItem = selection[index + 1];
      if (nextItem == null) {
        return;
      }

      consumptions.push({
        $key: item.id + nextItem.id,
        meter: service.meter,
        from_date: item.reading_date,
        from_amount: item.value,
        from_unit: item.unit,
        to_date: nextItem.reading_date,
        to_amount: nextItem.value,
        to_unit: nextItem.unit,
        billing_multiplier: service.meter_factor,
        diff: nextItem.value - item.value,
      });
    });

    service.consumptions = consumptions;
  };

  const services = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    services: services.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_services = async () => {
    return $fn.value
      .fetch('query_meter_readings', {
        parameters: {
          rental: props.rental,
          service_id: props.serviceId,
        },
        asResource: true,
      })
      .then((data) =>
        _.chain(data.services)
          .orderBy(['valid_from', 'inactive'], ['desc', 'asc'])
          .map(
            (svc) => (
              (svc.readings = _.reverse(svc.readings).map(
                (p, i) => ((p.selected = i < 2), p)
              )),
              onCheckChanged(svc),
              svc
            )
          )
          .value()
      );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'services' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { services: $$load_services },
    ux: {},
    dataRefs: {
      services,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'InvoiceLineMeterReadingSelector|Title',
        'Choose readings'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rental,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rental');
    }
  );
  watch(
    () => props.multiple,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('multiple');
    }
  );
  watch(
    () => props.serviceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('serviceId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_ys493lr98_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_4292833067371576_closeModal_w263133ub_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.scope.cons,
      },
    };
  };
  const act_4333930720033345_script_bn6int1t5_value_callable = async (
    $event
  ) => {
    return onCheckChanged($event.scope.item);
  };
  const act_4333930720033345_script_bn6int1t5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4333930720033345_script_bn6int1t5_value_callable($event);
      },
    };
  };
  const act_2140198529698299_closeModal_2si6azzqp_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_ys493lr98 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_ys493lr98_getActionArgs,
    });
  const inst_evh_7315092382398562_close_cmis8zxgt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_ys493lr98,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_cmis8zxgt],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_5jfqt5q17 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qvxs2rh34 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5jfqt5q17,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qvxs2rh34],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_amuklynd9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_m6qxr9k2t =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_amuklynd9,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_m6qxr9k2t],
    event: 'close',
  });
  const act_4292833067371576_closeModal_w263133ub =
    new actionTypes.CloseModalAction({
      actionArgs: act_4292833067371576_closeModal_w263133ub_getActionArgs,
    });
  const inst_evh_4292833067371576_clickPreventStop_34xofiqep =
    new actionTypes.EventHandlerImpl({
      action: act_4292833067371576_closeModal_w263133ub,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_4292833067371576_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4292833067371576_clickPreventStop_34xofiqep],
      event: 'click.prevent.stop',
    });
  const act_4333930720033345_script_bn6int1t5 = new actionTypes.ScriptAction({
    actionArgs: act_4333930720033345_script_bn6int1t5_getActionArgs,
  });
  const inst_evh_4333930720033345_change_3h1ws1bpz =
    new actionTypes.EventHandlerImpl({
      action: act_4333930720033345_script_bn6int1t5,
      event: 'change',
      displayName: 'script',
    });
  const inst_evh_4333930720033345_change = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4333930720033345_change_3h1ws1bpz],
    event: 'change',
  });
  const act_2140198529698299_closeModal_2si6azzqp =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_2si6azzqp_getActionArgs,
    });
  const inst_evh_2140198529698299_clickPreventStop_px6znynv1 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_2si6azzqp,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_px6znynv1],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_cmis8zxgt,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_qvxs2rh34,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_m6qxr9k2t,
    inst_evh_8564662037462133_close,
    inst_evh_4292833067371576_clickPreventStop_34xofiqep,
    inst_evh_4292833067371576_clickPreventStop,
    inst_evh_4333930720033345_change_3h1ws1bpz,
    inst_evh_4333930720033345_change,
    inst_evh_2140198529698299_clickPreventStop_px6znynv1,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_cmis8zxgt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_cmis8zxgt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_qvxs2rh34 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qvxs2rh34.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_m6qxr9k2t = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_m6qxr9k2t.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4292833067371576_clickPreventStop_34xofiqep = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4292833067371576_clickPreventStop_34xofiqep.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4292833067371576_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4292833067371576_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4333930720033345_change_3h1ws1bpz = async (event, scope) => {
    return await actions.inst_evh_4333930720033345_change_3h1ws1bpz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4333930720033345_change = async (event, scope) => {
    return await actions.inst_evh_4333930720033345_change.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_px6znynv1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_px6znynv1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="InvoiceLineMeterReadingSelector"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded && services">
      <BListGroup v-if="loaded">
        <BListGroupItem v-if="!services.length">
          <span>
            {{ pgettext('MeterRentalServices', 'No rental services.') }}
          </span>
        </BListGroupItem>
        <template v-for="item in services">
          <BListGroupItem
            :key="item.id"
            class="d-flex flex-column"
            v-if="services && services.length"
          >
            <ItpPanel collapsible initialState="collapsed">
              <template #caption>
                <div class="w-100">
                  <ItpBox alignItems="start" class="w-100">
                    <UcInactive :value="item.inactive"> </UcInactive>
                    <ItpBox direction="column" class="w-100">
                      <ItpBox class="W-100">
                        <ItpBox class="col-md-5">
                          <ItpText
                            :strikethrough="isServiceStale(item)"
                            class="fw-bold me-2"
                          >
                            {{ item.service && item.service.name }} ({{
                              item.billing_unit
                            }})
                          </ItpText>
                        </ItpBox>
                        <ItpBox class="col-md-4">
                          <UcValidityPeriod
                            :from="item.valid_from"
                            :to="item.valid_to"
                            class="ps-3"
                          >
                          </UcValidityPeriod>
                        </ItpBox>
                        <ItpBox
                          alignItems="center"
                          direction="column"
                          class="col-md-3"
                        >
                          <ItpBadge
                            :title="gettext('Billing multiplier')"
                            no-box-shadow
                            variant="light"
                            class="mx-3"
                          >
                            <ItpIcon name="fa-times"> </ItpIcon>
                            <ItpText :text="item.meter_factor"> </ItpText>
                          </ItpBadge>
                          <ItpBadge
                            variant="warning"
                            :text="pgettext('RentalService', 'Non billable')"
                            no-box-shadow
                            class="mx-3 mt-2"
                            v-if="item.non_billable"
                          >
                          </ItpBadge>
                        </ItpBox>
                      </ItpBox>
                      <ItpBox class="W-100">
                        <ItpText :text="item.notes" pre v-if="item.notes">
                        </ItpText>
                      </ItpBox>
                      <ItpBox>
                        <UcCompactMeterDisplay :payload="item.meter">
                        </UcCompactMeterDisplay>
                      </ItpBox>
                    </ItpBox>
                  </ItpBox>
                  <ul
                    class="list-group my-2"
                    v-if="item.consumptions && item.consumptions.length"
                  >
                    <li
                      :key="cons.$key"
                      :class="
                        'list-group-item list-group-item-' +
                        (item.inactive ? 'warning' : 'primary')
                      "
                      v-for="cons in item.consumptions"
                    >
                      <ItpBox direction="row">
                        <div>
                          <strong class="me-2"
                            >{{ cons.from_amount }} {{ cons.from_unit }}</strong
                          >
                          <small class="me-2"
                            >({{ date(cons.from_date) }})</small
                          >
                          -
                          <strong class="me-2"
                            >{{ cons.to_amount }} {{ cons.to_unit }}</strong
                          >
                          <small class="me-2">({{ date(cons.to_date) }})</small>
                          =
                          <strong>{{ cons.diff }} {{ cons.to_unit }}</strong>
                        </div>
                        <ItpButton
                          size="sm"
                          class="ms-auto"
                          @click.prevent.stop="
                            evh_4292833067371576_clickPreventStop($event, {
                              item,
                              cons,
                            })
                          "
                        >
                          Select
                        </ItpButton>
                      </ItpBox>
                    </li>
                  </ul>
                </div>
              </template>
              <table class="table table-condensed table-sm">
                <thead>
                  <tr>
                    <th></th>
                    <th>
                      {{
                        pgettext(
                          'InvoiceLineMeterReadingSelector|ColHead',
                          'Value'
                        )
                      }}
                    </th>
                    <th>
                      {{
                        pgettext(
                          'InvoiceLineMeterReadingSelector|ColHead',
                          'Diff'
                        )
                      }}
                    </th>
                    <th>
                      {{
                        pgettext(
                          'InvoiceLineMeterReadingSelector|ColHead',
                          'Date'
                        )
                      }}
                    </th>
                    <th>
                      {{
                        pgettext(
                          'InvoiceLineMeterReadingSelector|ColHead',
                          'Notes'
                        )
                      }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr :key="reading.id" v-for="reading in item.readings">
                    <td>
                      <BFormCheckbox
                        :name="reading.id"
                        v-model="reading.selected"
                        :id="reading.id"
                        @change="
                          evh_4333930720033345_change($event, { item, reading })
                        "
                      >
                      </BFormCheckbox>
                    </td>
                    <td>
                      <strong>{{ reading.value }} {{ reading.unit }}</strong>
                    </td>
                    <td class="text-muted">
                      {{ reading.diff }} {{ reading.unit }}
                    </td>
                    <td>
                      {{ date(reading.reading_date) }}
                      <small>{{ time(reading.reading_time) }}</small>
                    </td>
                    <td>
                      {{ reading.notes }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </ItpPanel>
          </BListGroupItem>
        </template>
      </BListGroup>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
